/**
 * "Ekip Sistem Kullanımı" bölümünün gövdesi.
 *
 * Veriyi hesaplamaz — `useTeamActivity` çıktısını alır ve gösterir. Bölüm
 * başlığı, tarih filtresi ve aç/kapa düğmesi diğer bölümlerle aynı görünsün
 * diye üst sayfada (SMOLeadReports) kalır.
 */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { LuArrowDown, LuArrowUp, LuArrowUpDown, LuChevronDown, LuCircle, LuClock, LuDownload, LuLoaderCircle, LuLogIn, LuRefreshCw, LuTriangleAlert, LuUserRoundCheck, LuUsers } from 'react-icons/lu';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import * as XLSX from 'xlsx';
import Button from '../../components/ui/Button';
import { HelpNote } from '../ProfilePage/components/ProfileUi';
import {
  formatDuration,
  formatDateTime,
  formatDayLabel,
  toHours,
} from '../../utils/onlineActivity';
import { ACTIVITY_WINDOW_DAYS } from './useTeamActivity';
import useSmoText from './smoText';
import useActivityFormat from './parts/activityFormat';
import { ChartCard, useChartTheme } from './parts/chartKit';
import { PanelTile } from './parts/PanelTile';

const CHART_COLORS = ['#4299e1', '#68d391', '#9f7aea', '#f6ad55', '#4fd1c5', '#fc8181'];
const TOP_EMPLOYEE_BARS = 12;

const TeamActivityPanel = ({ data, startDate, endDate, smoName }) => {
  const { label, locale } = useSmoText();
  const format = useActivityFormat();
  const chart = useChartTheme();
  const { rows, summary, dailyTotals, loading, error, reload, hasHistory, windowStartDay } = data;
  const [sortKey, setSortKey] = useState('rangeMs');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedId, setExpandedId] = useState(null);

  const sortedRows = useMemo(() => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (sortKey === 'name') return direction * a.name.localeCompare(b.name, 'tr');
      if (sortKey === 'isOnline') return direction * ((a.isOnline ? 1 : 0) - (b.isOnline ? 1 : 0));
      return direction * ((a[sortKey] || 0) - (b[sortKey] || 0));
    });
  }, [rows, sortKey, sortDirection]);

  const topByTime = useMemo(
    () =>
      [...rows]
        .filter((r) => r.rangeMs > 0)
        .sort((a, b) => b.rangeMs - a.rangeMs)
        .slice(0, TOP_EMPLOYEE_BARS)
        .map((r) => ({
          name: r.name.length > 18 ? `${r.name.substring(0, 17)}…` : r.name,
          fullName: r.name,
          Saat: toHours(r.rangeMs),
        })),
    [rows]
  );

  const dailyChart = useMemo(
    () =>
      dailyTotals.map((d) => ({
        label: formatDayLabel(d.date),
        date: d.date,
        Saat: toHours(d.onlineMs),
        people: d.people,
      })),
    [dailyTotals]
  );

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection(key === 'name' ? 'asc' : 'desc');
    }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return <LuArrowUpDown aria-hidden="true" className="tw-h-3 tw-w-3 tw-opacity-40" />;
    return sortDirection === 'asc' ? <LuArrowUp aria-hidden="true" className="tw-h-3 tw-w-3" /> : <LuArrowDown aria-hidden="true" className="tw-h-3 tw-w-3" />;
  };

  // Excel stays Turkish (like every export in the portal)
  const downloadExcel = () => {
    const sheet = XLSX.utils.json_to_sheet(
      sortedRows.map((r) => ({
        'Çalışan': r.name,
        'E-posta': r.email,
        'Durum': r.isOnline ? 'Çevrimiçi' : 'Çevrimdışı',
        'Aralıkta Süre': formatDuration(r.rangeMs),
        'Aralıkta Süre (saat)': toHours(r.rangeMs),
        'Aktif Gün': r.activeDays,
        'Günlük Ortalama': formatDuration(r.avgActiveDayMs),
        'Giriş Sayısı': r.logins,
        'Oturum Sayısı': r.sessions,
        'Son Aktivite': formatDateTime(r.lastActivity),
        'Toplam Süre (tüm zamanlar)': formatDuration(r.totalOnlineMs),
      }))
    );
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Sistem Kullanımı');
    const range = `${startDate || 'baslangic'}_${endDate || 'bugun'}`;
    XLSX.writeFile(book, `ekip-sistem-kullanimi_${(smoName || 'smo').replace(/\s+/g, '-')}_${range}.xlsx`);
  };

  if (loading && !hasHistory) {
    return (
      <p className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-8 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
        <LuLoaderCircle aria-hidden="true" className="tw-h-5 tw-w-5 tw-animate-spin tw-text-teal-500" />
        {label('activity.loading')}
      </p>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-8 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
        <LuTriangleAlert aria-hidden="true" className="tw-h-5 tw-w-5 tw-text-amber-500" />
        {label('activity.noWorkers')}
      </p>
    );
  }

  const columns = [
    ['name', label('activity.columns.worker')],
    ['isOnline', label('activity.columns.status')],
    ['rangeMs', label('activity.columns.range')],
    ['activeDays', label('activity.columns.activeDays')],
    ['avgActiveDayMs', label('activity.columns.dailyAvg')],
    ['logins', label('activity.columns.logins')],
    ['lastActivity', label('activity.columns.lastActivity')],
    ['totalOnlineMs', label('activity.columns.total')],
  ];

  return (
    <div className="tw-space-y-4">
      {error && <HelpNote tone="danger">{label('activity.error')}</HelpNote>}

      {/* ── Özet kutuları ── */}
      <div className="tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-3 xl:tw-grid-cols-5">
        <PanelTile icon={LuCircle} iconClass="tw-text-emerald-500 [&>*]:tw-fill-emerald-500" value={summary.onlineCount} of={summary.teamSize} title={label('activity.tiles.online')} />
        <PanelTile icon={LuClock} value={format.duration(summary.totalMs)} title={label('activity.tiles.total')} />
        <PanelTile icon={LuUserRoundCheck} value={format.duration(summary.avgPerPersonMs)} title={label('activity.tiles.avg')} />
        <PanelTile icon={LuLogIn} value={summary.totalLogins} title={label('activity.tiles.logins')} />
        <PanelTile icon={LuUsers} value={summary.activeCount} of={summary.teamSize} title={label('activity.tiles.active')} />
      </div>

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <span className="tw-min-w-0 tw-flex-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
          {label('activity.window', { days: ACTIVITY_WINDOW_DAYS, date: new Date(windowStartDay).toLocaleDateString(locale) })}
        </span>
        <Button size="sm" onClick={reload} disabled={loading}>
          <LuRefreshCw aria-hidden="true" className={`tw-h-4 tw-w-4 ${loading ? 'tw-animate-spin' : ''}`} />
          {label('header.refresh')}
        </Button>
        <Button size="sm" variant="success" onClick={downloadExcel}>
          <LuDownload aria-hidden="true" className="tw-h-4 tw-w-4" />
          Excel
        </Button>
      </div>

      {/* ── Çalışan tablosu ── */}
      <div className="tw-relative tw-overflow-x-auto tw-rounded-2xl tw-border tw-border-slate-200/80 glass-scrollbar dark:tw-border-white/10">
        <table className="tw-w-full tw-border-collapse tw-text-sm">
          <thead className="tw-bg-slate-50/90 dark:tw-bg-white/[0.03]">
            <tr>
              {columns.map(([key, name]) => (
                <th key={key} scope="col" aria-sort={sortKey === key ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined} className="tw-whitespace-nowrap tw-px-3 tw-py-2 tw-text-left">
                  <button type="button" onClick={() => toggleSort(key)} className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-lg tw-bg-transparent tw-px-1 tw-py-1 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 hover:tw-text-slate-900 dark:tw-text-slate-400 dark:hover:tw-text-white">
                    {name}
                    {sortIcon(key)}
                  </button>
                </th>
              ))}
              <th scope="col" className="tw-w-8"><span className="tw-sr-only">{label('table.expand')}</span></th>
            </tr>
          </thead>
          <tbody className="tw-divide-y tw-divide-slate-200/70 tw-text-slate-700 dark:tw-divide-white/[0.06] dark:tw-text-slate-200">
            {sortedRows.map((row) => {
              const expanded = expandedId === row.id;
              const maxDayMs = Math.max(1, ...row.days.map((d) => d.onlineMs));
              return (
                <React.Fragment key={row.id}>
                  <tr onClick={() => setExpandedId(expanded ? null : row.id)} aria-expanded={expanded} className={`tw-cursor-pointer tw-transition-colors ${expanded ? 'tw-bg-sky-500/[0.06]' : 'hover:tw-bg-slate-900/[0.02] dark:hover:tw-bg-white/[0.03]'}`}>
                    <td className="tw-px-4 tw-py-2.5">
                      <span className="tw-block tw-whitespace-nowrap tw-font-semibold tw-text-slate-900 dark:tw-text-white">{row.name}</span>
                      <span className="tw-block tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{row.email || '—'}</span>
                    </td>
                    <td className="tw-whitespace-nowrap tw-px-4 tw-py-2.5">
                      {row.isOnline ? (
                        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-emerald-700 dark:tw-text-emerald-300">
                          <span aria-hidden="true" className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-emerald-500" />
                          {label('activity.online')}
                          {row.currentSessionMs > 0 && <small className="tw-font-normal">({format.duration(row.currentSessionMs)})</small>}
                        </span>
                      ) : (
                        <span className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('activity.offline')}</span>
                      )}
                    </td>
                    <td className="tw-whitespace-nowrap tw-px-4 tw-py-2.5 tw-font-semibold tw-tabular-nums">{format.duration(row.rangeMs)}</td>
                    <td className="tw-px-4 tw-py-2.5 tw-tabular-nums">{row.activeDays}</td>
                    <td className="tw-whitespace-nowrap tw-px-4 tw-py-2.5 tw-tabular-nums">{format.duration(row.avgActiveDayMs)}</td>
                    <td className="tw-px-4 tw-py-2.5 tw-tabular-nums">{row.logins}</td>
                    <td className="tw-whitespace-nowrap tw-px-4 tw-py-2.5" title={format.dateTime(row.lastActivity)}>{format.relative(row.lastActivity)}</td>
                    <td className="tw-whitespace-nowrap tw-px-4 tw-py-2.5 tw-tabular-nums">{format.duration(row.totalOnlineMs)}</td>
                    <td className="tw-px-2 tw-py-2.5 tw-text-slate-400">
                      <LuChevronDown aria-hidden="true" className={`tw-h-4 tw-w-4 tw-transition-transform ${expanded ? 'tw-rotate-180' : ''}`} />
                    </td>
                  </tr>

                  {expanded && (
                    <tr className="tw-bg-slate-50/70 dark:tw-bg-white/[0.02]">
                      <td colSpan={9} className="tw-space-y-3 tw-whitespace-normal tw-px-4 tw-py-4">
                        <dl className="tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-4">
                          {[
                            [label('activity.detail.sessions'), row.sessions],
                            [label('activity.detail.totalSessions'), row.sessionCount],
                            [label('activity.detail.avgSession'), format.duration(row.sessionCount > 0 ? row.totalOnlineMs / row.sessionCount : 0)],
                            [label('activity.detail.lastActivity'), format.dateTime(row.lastActivity)],
                          ].map(([name, value]) => (
                            <div key={name} className="tw-rounded-xl tw-border tw-border-slate-200/80 tw-bg-white/80 tw-p-2.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                              <dt className="tw-text-[11px] tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{name}</dt>
                              <dd className="tw-mt-0.5 tw-text-sm tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{value}</dd>
                            </div>
                          ))}
                        </dl>
                        <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-text-slate-400">{label('activity.detail.daily')}</p>
                        {row.days.length === 0 ? (
                          <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('activity.detail.noDays')}</p>
                        ) : (
                          <div className="tw-space-y-1.5">
                            {row.days.map((day) => (
                              <div key={day.date} className="tw-grid tw-grid-cols-[3.5rem_1fr_5rem] tw-items-center tw-gap-2 tw-text-xs">
                                <span className="tw-tabular-nums tw-text-slate-500 dark:tw-text-slate-400">{formatDayLabel(day.date)}</span>
                                <span className="tw-h-2 tw-overflow-hidden tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10">
                                  <span className="tw-block tw-h-full tw-rounded-full tw-bg-sky-500" style={{ width: `${(day.onlineMs / maxDayMs) * 100}%` }} />
                                </span>
                                <span className="tw-text-right tw-tabular-nums tw-text-slate-700 dark:tw-text-slate-200">{format.duration(day.onlineMs)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Grafikler ── */}
      <div className="tw-grid tw-gap-4 lg:tw-grid-cols-2">
        <ChartCard title={label('activity.dailyChart')}>
          {dailyChart.length === 0 ? (
            <p className="tw-py-6 tw-text-center tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('activity.noRecords')}</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={dailyChart}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="label" tick={chart.tick} interval="preserveStartEnd" />
                <YAxis unit={label('activity.hourUnit')} tick={chart.tick} />
                <Tooltip
                  formatter={(value, _key, item) => [label('activity.dailyTooltip', { hours: value, people: item?.payload?.people || 0 }), label('activity.totalLabel')]}
                  {...chart.tooltip}
                />
                <Bar dataKey="Saat" fill="#4299e1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title={label('activity.perEmployeeChart')}>
          {topByTime.length === 0 ? (
            <p className="tw-py-6 tw-text-center tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('activity.noRecords')}</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(280, topByTime.length * 34)}>
              <BarChart data={topByTime} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis type="number" unit={label('activity.hourUnit')} tick={chart.tick} />
                <YAxis dataKey="name" type="category" tick={chart.tick} width={120} />
                <Tooltip
                  labelFormatter={(name) => topByTime.find((t) => t.name === name)?.fullName || name}
                  formatter={(value) => [label('activity.hours', { value }), label('activity.durationLabel')]}
                  {...chart.tooltip}
                />
                <Bar dataKey="Saat" radius={[0, 4, 4, 0]}>
                  {topByTime.map((entry, index) => (
                    <Cell key={entry.fullName} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <p className="tw-text-xs tw-leading-relaxed tw-text-slate-500 dark:tw-text-slate-400">{label('activity.footNote')}</p>
    </div>
  );
};

TeamActivityPanel.propTypes = {
  data: PropTypes.object.isRequired,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  smoName: PropTypes.string,
};

export default TeamActivityPanel;
